import { ChevronDown, Inbox, Send } from "lucide-react";
import type { DentalEmailMetadata } from "./dentalConversationExamples";

type DentalEmailMessageProps = {
  email: DentalEmailMetadata;
  text: string;
  speaker: "patient" | "assistant";
  initiallyOpen?: boolean;
};

export function DentalEmailMessage({ email, text, speaker, initiallyOpen = false }: DentalEmailMessageProps) {
  const isSent = speaker === "assistant";
  const DirectionIcon = isSent ? Send : Inbox;

  return (
    <li className={isSent ? "ml-3" : "mr-3"}>
      <details
        open={initiallyOpen}
        className={`group/email min-w-0 rounded-[8px] border bg-white ${isSent ? "border-[#e1e8e5] open:border-[#cfe6d9]" : "border-[#e1e7ef] open:border-[#cedeed]"}`}
      >
        <summary aria-label={`${isSent ? "Sent" : "Received"}: ${email.subject}, ${email.time}`} className="flex min-h-9 cursor-pointer list-none items-center gap-2 rounded-[8px] px-2.5 py-2 outline-none transition-colors hover:bg-[#fafcfd] focus-visible:ring-1 focus-visible:ring-[#8cafd3] motion-reduce:transition-none [&::-webkit-details-marker]:hidden">
          <ChevronDown aria-hidden="true" strokeWidth={1.5} className="h-2.5 w-2.5 shrink-0 -rotate-90 text-[#8a99aa] transition-transform group-open/email:rotate-0 motion-reduce:transition-none" />
          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10.5px] font-medium leading-[14px] ${isSent ? "bg-[#edf8f2] text-[#398362]" : "bg-[#edf5fc] text-[#4e7eaa]"}`}>
            <DirectionIcon aria-hidden="true" strokeWidth={1.5} className="h-2.5 w-2.5" />
            {isSent ? "Sent" : "Received"}
          </span>
          <span className="min-w-0 flex-1 truncate text-[11px] font-medium leading-4 text-[#465264] group-open/email:hidden">{email.subject}</span>
          <span aria-hidden="true" className="hidden min-w-0 flex-1 truncate text-[10.5px] leading-4 text-[#8a97a6] group-open/email:hidden xl:block">{text}</span>
          {isSent ? <span className="hidden rounded-full bg-[#f2f4f7] px-1.5 py-0.5 text-[10.5px] leading-[14px] text-[#6f7c8d] group-open/email:inline-flex">AI reply</span> : null}
          <time dateTime={email.dateTime} className="ml-auto shrink-0 whitespace-nowrap text-[10.5px] leading-4 tabular-nums text-[#8290a1]">{email.time}</time>
        </summary>

        <div className="border-t border-[#edf0f3] px-3 pb-2.5 pt-2">
          <p className="break-words text-[12px] font-semibold leading-4 text-[#354152]">{email.subject}</p>
          <dl className="mt-1.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-1.5 gap-y-0.5 text-[10.5px] leading-[15px] text-[#7a8798]">
            <dt>From</dt><dd className="break-words">{email.from}</dd>
            <dt>To</dt><dd className="break-words">{email.to}</dd>
          </dl>
          <p className="mt-2 whitespace-pre-line break-words text-[12px] leading-[1.6] text-[#3f4b5a]">{text}</p>
          {email.quotedMessage ? (
            <details className="group/quote mt-2">
              <summary className="flex min-h-7 w-fit cursor-pointer list-none items-center gap-1 rounded text-[10.5px] leading-4 text-[#7a8798] outline-none hover:text-[#465264] focus-visible:ring-1 focus-visible:ring-[#8cafd3] [&::-webkit-details-marker]:hidden">
                <ChevronDown aria-hidden="true" className="h-2.5 w-2.5 -rotate-90 transition-transform group-open/quote:rotate-0 motion-reduce:transition-none" />
                Quoted message
              </summary>
              <blockquote className="mt-1 border-l-2 border-[#dde5ed] pl-2 text-[11px] leading-[17px] text-[#7b8796]">
                <p className="mb-1 text-[10.5px]">{email.quotedMessage.from} · {email.quotedMessage.time}</p>
                <p className="break-words">{email.quotedMessage.text}</p>
              </blockquote>
            </details>
          ) : null}
        </div>
      </details>
    </li>
  );
}
